import { Suspense, useEffect } from "react";

import PageSkeleton from "@/components/loading/PageSkeleton";

import type { ReactNode } from "react";

interface RouteSuspenseProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Component that ensures scroll to top when content loads
 */
function ScrollToTopOnMount({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Scroll to top when content mounts (after Suspense resolves)
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });

      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
    };

    // Scroll immediately
    scrollToTop();

    // Scroll after layout is calculated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToTop();

        // One more scroll after a short delay to catch any late-rendering
        setTimeout(scrollToTop, 100);
      });
    });
  }, []);

  return <>{children}</>;
}

/**
 * RouteSuspense - Wrapper component for lazy-loaded routes
 * Provides consistent loading UI during route code splitting
 */
export default function RouteSuspense({ children, fallback }: RouteSuspenseProps) {
  return (
    <Suspense fallback={fallback || <PageSkeleton />}>
      <ScrollToTopOnMount>{children}</ScrollToTopOnMount>
    </Suspense>
  );
}
